import { observer } from 'mobx-react-lite';
import React from 'react';

import Typography from '@smart-tv/components/typography/typography';
import testIds from '@smart-tv/e2e/shared/variables';
import getDate from '@smart-tv/utils/date/get-date';
import getDayName from '@smart-tv/utils/date/get-day-name';
import getHours from '@smart-tv/utils/date/get-hours';
import getMinutes from '@smart-tv/utils/date/get-minutes';
import getMonthName from '@smart-tv/utils/date/get-month-name/get-month-name';
import getTime from '@smart-tv/utils/date/get-time';
import isToday from '@smart-tv/utils/date/is-today';
import isTomorrow from '@smart-tv/utils/date/is-tomorrow';
import isWithinInterval from '@smart-tv/utils/date/is-within-interval';
import isYesterday from '@smart-tv/utils/date/is-yesterday';
import { rootStore } from '../_stores/store-root';

export const formatAirtime = (startTime: Date) => {
  const hours = getHours(startTime);
  const minutes = getMinutes(startTime);

  const formattedHours = hours < 10 ? `0${hours}` : hours;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  return `${formattedHours}:${formattedMinutes}`;
};

const formatFullAirtime = (startTime: Date) => {
  if (isNaN(startTime.getTime())) {
    return null;
  }

  const withinPastWeek = isWithinInterval(startTime, {
    start: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
    end: new Date()
  });

  const dayName = getDayName(startTime, { titleCase: withinPastWeek });
  const date = getDate(startTime);
  const monthName = getMonthName(startTime, { abbreviate: true });
  const time = formatAirtime(startTime);

  return withinPastWeek
    ? `Sendt i ${dayName.toLowerCase()}s kl. ${time}`
    : `${date}. ${monthName}. kl. ${time}`;
};

const endOfDay = new Date();
endOfDay.setHours(23, 59, 59, 999);

export const renderAirtime = (startTime: Date, endTime?: Date, short?: boolean) => {
  if (endTime && isWithinInterval(Date.now(), { start: startTime, end: endTime })) {
    return 'Sendes nu';
  }

  if (startTime && getTime(startTime) > Date.now() && getTime(startTime) < endOfDay.getTime()) {
    return `${short ? 'I dag' : 'Sendes i dag'} kl. ${formatAirtime(startTime)}`;
  }

  if (isToday(startTime)) {
    return `Sendt i dag kl. ${formatAirtime(startTime)}`;
  }

  if (isTomorrow(startTime)) {
    return `I morgen kl. ${formatAirtime(startTime)}`;
  }

  if (isYesterday(startTime)) {
    return `Sendt i går kl. ${formatAirtime(startTime)}`;
  }

  return formatFullAirtime(startTime);
};

export const SheetDescriptionInfoAirtime = observer(() => {
  const { infoBlock, readableAirTimeOrRecordingTime } = rootStore.sheetStore.sheet;
  const { startTime, duration } = infoBlock || {};

  if (!readableAirTimeOrRecordingTime && (!startTime || !duration)) {
    return null;
  }

  return (
    <Typography variant="caption" data-testid={testIds.programsheet.info.airtime}>
      {readableAirTimeOrRecordingTime ??
        renderAirtime(new Date(startTime * 1000), new Date(startTime * 1000 + duration * 1000))}
    </Typography>
  );
});

export default SheetDescriptionInfoAirtime;
